import { useQuery } from '@apollo/client';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type {
  Enterprise_Risk_Bool_Exp,
  GetEnterpriseRisksFlatQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetEnterpriseRisksFlatDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

import { useGetEnterpriseRisksRegister as useGetEnterpriseRisksRegisterTRPC } from './useGetEnterpriseRisksRegisterTRPC';

export const useGetEnterpriseRisksRegister = (
  onCompleted?: (data: GetEnterpriseRisksFlatQuery | undefined) => void,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const { addNotification } = useNotifications();

  const where = useEntityWhereFilter<Enterprise_Risk_Bool_Exp>(
    Parent_Type_Enum.EnterpriseRisk
  );

  const {
    data: graphqlData,
    loading: graphqlLoading,
    refetch: graphqlRefetch,
  } = useQuery(GetEnterpriseRisksFlatDocument, {
    variables: { where },
    fetchPolicy: 'no-cache',
    skip: trpcEnabled || skip, // Skip GraphQL query when TRPC is enabled
    onError: (error) => {
      if (!trpcEnabled) {
        addNotification({
          type: 'error',
          content: <>{error.message}</>,
        });
      }
    },
    onCompleted,
  });

  const {
    data: trpcData,
    loading: trpcLoading,
    refetch: trpcRefetch,
  } = useGetEnterpriseRisksRegisterTRPC(onCompleted, skip);

  // Return appropriate data based on feature flag
  if (trpcEnabled) {
    return {
      loading: trpcLoading,
      data: trpcData,
      refetch: () => trpcRefetch(),
    };
  }

  return {
    loading: graphqlLoading,
    data: graphqlData,
    refetch: () => graphqlRefetch(),
  };
};
